import { CheckCircleOutline } from '@mui/icons-material';
import { Badge, Box, ButtonBase, styled, Typography } from '@mui/material';
import React, { FC, useState } from 'react';
import { useNavigate } from 'react-router';
import { useAppSelector } from '@acorn-portal/shared/src/hooks/reduxHook';
import { Product } from '@acorn-portal/shared/src/services/interface/Shared';
import HelperModal from '../modal/HelperModal';

type Modal = {
	title: string;
	description: string;
};

interface IProps {
	Icon: React.ReactNode;
	text: string;
	href: string;
	isComplete?: boolean;
	disabled?: boolean;
	canChange?: boolean;
	modal?: Modal;
}

const IconButton = styled(ButtonBase)(({ theme }) => ({
	borderColor: theme.palette.divider,
	borderWidth: '2px',
	borderStyle: 'solid',
	borderRadius: '8px',
	svg: {
		height: '120px',
	},
	[theme.breakpoints.up('md')]: {
		width: '250px',
		height: '250px',
	},
	[theme.breakpoints.down('md')]: {
		width: '100%',
		svg: {
			height: '60px',
			width: 'initial',
			minWidth: '90px',
			paddingRight: 1,
		},
	},
}));

const LargeIconButton: FC<IProps> = ({ Icon, text, href, isComplete, disabled, modal, canChange = true }) => {
	const navigate = useNavigate();
	const [open, toggleModal] = useState(false);
	const policy = useAppSelector((redux) => redux.fullPolicy);
	const isCV = policy.product === Product.COMMERCIAL_VEHICLE;

	const handleOnClick = () => {
		if (canChange) {
			navigate(href);
		}
		toggleModal(true);
	};

	return (
		<Box sx={{ marginBottom: '20px' }}>
			<Badge
				color='success'
				invisible={!isComplete}
				anchorOrigin={{
					vertical: 'bottom',
					horizontal: 'right',
				}}
				sx={{
					width: '100%',
					'& .BaseBadge-badge': {
						height: '36px',
						borderRadius: '36px',
					},
				}}
				badgeContent={<CheckCircleOutline />}
			>
				<IconButton
					disabled={disabled}
					focusRipple
					onClick={handleOnClick}
					sx={
						isComplete
							? {
									borderColor: 'green',
									borderWidth: '4px',
								}
							: null
					}
				>
					<Box
						sx={(theme) => ({
							display: 'flex',
							alignItems: 'center',
							flexDirection: 'column',
							'*': {
								fill: disabled ? 'gray !important' : 'secondary.main',
							},

							[theme.breakpoints.down('md')]: {
								flexDirection: 'row',
								width: '100%',
								p: 1.5,
							},
						})}
					>
						{isCV ? (
							<Box
								sx={{
									display: 'flex',
									maxWidth: '80%',
									alignSelf: 'center',
								}}
							>
								{Icon}
							</Box>
						) : (
							<Box>{Icon}</Box>
						)}

						<Typography
							component='span'
							variant='h6'
							color='secondary'
							sx={{
								position: 'relative',
								wordWrap: 'normal',
								maxWidth: '200px',
								margin: 1,
								alignSelf: 'center',
								color: disabled ? 'gray' : 'secondary.main',
							}}
						>
							{text}
						</Typography>
					</Box>
				</IconButton>
			</Badge>
			{modal && (
				<HelperModal
					open={open}
					onClose={() => {
						toggleModal(false);
					}}
					{...modal}
				/>
			)}
		</Box>
	);
};

export default LargeIconButton;
